import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Trash, Pencil, ArrowsClockwise, Receipt, Globe, HardDrives, CalendarBlank } from '@phosphor-icons/react';
import SearchableSelect from '../components/SearchableSelect';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKET_META = {
  expired:  { label: 'Expired',         color: '#b91c1c', bg: '#fee2e2' },
  urgent:   { label: '≤7 days',         color: '#c2410c', bg: '#ffedd5' },
  soon:     { label: '8–15 days',       color: '#a16207', bg: '#fef3c7' },
  upcoming: { label: '16–30 days',      color: '#1d4ed8', bg: '#dbeafe' },
  ok:       { label: '>30 days',        color: '#15803d', bg: '#dcfce7' },
  unknown:  { label: 'No date',         color: '#475569', bg: '#f1f5f9' },
};

const HOSTED_BY = [
  { v: 'own',    l: 'OWN (Dextra)' },
  { v: 'client', l: 'Client Server' },
];

const empty = () => ({
  company_name: '', server_name: '', email: '', hosted_by: 'own',
  amount: 0, currency: 'INR', phone: '', hosting_date: '',
  last_renewal_date: '', expiring_date: '', remarks: '', client_id: '',
  reminder_lead_days: [15, 7],
});

export default function HostingsPage() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = { withCredentials: true };
      const [a, b, c] = await Promise.all([
        axios.get(`${API_URL}/api/hostings`, cfg),
        axios.get(`${API_URL}/api/hostings/summary`, cfg).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/clients`, cfg).catch(() => ({ data: [] })),
      ]);
      setList(a.data || []);
      setSummary(b.data);
      setClients(c.data || []);
    } catch (e) {
      toast.error('Load failed: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty()); setShowForm(true); };
  const openEdit = (h) => { setEditing(h); setForm({ ...empty(), ...h }); setShowForm(true); };

  const save = async () => {
    if (!form.company_name?.trim() || !form.server_name?.trim() || !form.expiring_date) {
      toast.error('Company, Server, and Expiring Date are required'); return;
    }
    try {
      const body = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editing) {
        await axios.put(`${API_URL}/api/hostings/${editing.id}`, body, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${API_URL}/api/hostings`, body, { withCredentials: true });
        toast.success('Hosting added');
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete hosting for "${h.company_name}"?`)) return;
    await axios.delete(`${API_URL}/api/hostings/${h.id}`, { withCredentials: true });
    toast.success('Deleted'); load();
  };

  const renewNow = async (h) => {
    const renewed_on = new Date().toISOString().slice(0, 10);
    await axios.post(`${API_URL}/api/hostings/${h.id}/mark-renewed`, { renewed_on, months: 12 }, { withCredentials: true });
    toast.success('Marked renewed — expiry pushed by 1 year'); load();
  };

  const runRemindNow = async () => {
    await axios.post(`${API_URL}/api/hostings/auto-remind/run`, {}, { withCredentials: true });
    toast.success('Reminder scan complete');
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((h) => {
      if (bucketFilter !== 'all' && h.expiry_status !== bucketFilter) return false;
      if (!q) return true;
      const blob = `${h.company_name} ${h.server_name} ${h.email || ''} ${h.phone || ''} ${h.remarks || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [list, search, bucketFilter]);

  const { paged: pagedHostings, controls: hostingControls, startIdx: hostingStart } = usePagination(filtered, { storageKey: 'hostings-page-size', testIdPrefix: 'hosting' });

  return (
    <div className="space-y-4" data-testid="hostings-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--dx-text1)' }}>
            <HardDrives size={26} weight="duotone" /> Hosting & Domain Renewals
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Track every hosting/domain you manage for clients. Auto-reminders 15 + 7 days before expiry.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runRemindNow} data-testid="hostings-remind-now">
            <ArrowsClockwise size={14} className="mr-1.5" /> Scan reminders now
          </Button>
          <Button size="sm" onClick={openNew} className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="hostings-new">
            <Plus size={14} className="mr-1.5" /> New Hosting
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {['expired', 'urgent', 'soon', 'upcoming', 'ok'].map((k) => {
            const m = BUCKET_META[k];
            const data = summary.by_bucket[k] || { count: 0, total_amount: 0 };
            return (
              <Card
                key={k}
                className={`p-3 cursor-pointer transition ${bucketFilter === k ? 'ring-2' : ''}`}
                style={bucketFilter === k ? { borderColor: m.color, boxShadow: `0 0 0 2px ${m.color}` } : undefined}
                onClick={() => setBucketFilter(bucketFilter === k ? 'all' : k)}
                data-testid={`hostings-bucket-${k}`}
              >
                <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: m.color }}>{m.label}</div>
                <div className="text-2xl font-bold" style={{ color: m.color }}>{data.count}</div>
                <div className="text-[10px] text-gray-500">₹{data.total_amount.toLocaleString('en-IN')}</div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <Card className="p-3 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search by company, server, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[240px] text-sm px-3 py-2 border rounded outline-none focus:ring-2 focus:ring-emerald-500/30"
          data-testid="hostings-search"
        />
        {(search || bucketFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { setSearch(''); setBucketFilter('all'); }}
            className="text-xs px-2 py-1.5 rounded border bg-white hover:bg-gray-50"
            data-testid="hostings-clear-filters"
          >Clear</button>
        )}
      </Card>

      {/* List */}
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--dx-bg)' }}>
            <tr className="text-left">
              <th className="px-3 py-2 text-[11px] uppercase">#</th>
              <th className="px-3 py-2 text-[11px] uppercase">Company / Server</th>
              <th className="px-3 py-2 text-[11px] uppercase">Hosted By</th>
              <th className="px-3 py-2 text-[11px] uppercase text-right">Amount</th>
              <th className="px-3 py-2 text-[11px] uppercase">Expires</th>
              <th className="px-3 py-2 text-[11px] uppercase">Days Left</th>
              <th className="px-3 py-2 text-[11px] uppercase text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-gray-400 py-6">Loading…</td></tr>
            ) : pagedHostings.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-gray-400 py-6">
                <Globe size={28} className="mx-auto mb-2 text-gray-300" />
                {list.length === 0 ? 'No hosting records yet. Click "New Hosting" to add the first one.' : 'No matches for your filter.'}
              </td></tr>
            ) : pagedHostings.map((h, i) => {
              const m = BUCKET_META[h.expiry_status] || BUCKET_META.unknown;
              return (
                <tr key={h.id} className="border-t" data-testid={`hosting-row-${h.id}`}>
                  <td className="px-3 py-2 text-gray-400">{hostingStart + i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-bold">{h.company_name}</div>
                    <div className="text-[11px] text-gray-500">{h.server_name}</div>
                    {h.email && <div className="text-[10px] text-gray-400">{h.email}</div>}
                  </td>
                  <td className="px-3 py-2 text-[11px] uppercase font-semibold">
                    {h.hosted_by === 'own' ? <span className="text-emerald-700">OWN</span> : <span className="text-amber-700">CLIENT</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">₹{(h.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 font-mono text-[12px]">{h.expiring_date || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-bold px-2 py-1 rounded uppercase" style={{ background: m.bg, color: m.color }}>
                      {h.days_until_expiry == null ? '—' : h.days_until_expiry < 0 ? `Expired ${-h.days_until_expiry}d ago` : `${h.days_until_expiry}d`}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => renewNow(h)} title="Mark renewed (push expiry +1y)" data-testid={`hosting-renew-${h.id}`}>
                        <Receipt size={14} className="mr-1" /> Renew
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(h)} data-testid={`hosting-edit-${h.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(h)} data-testid={`hosting-delete-${h.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {hostingControls}
      </Card>

      {/* New / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div className="text-base font-bold">{editing ? 'Edit Hosting' : 'New Hosting Record'}</div>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700">✕</button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Company Name *</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="hosting-company" />
              </div>
              <div>
                <Label className="text-xs">Server Name *</Label>
                <Input value={form.server_name} onChange={(e) => setForm({ ...form, server_name: e.target.value })} data-testid="hosting-server" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="hosting-email" />
              </div>
              <div>
                <Label className="text-xs">Hosted By *</Label>
                <select
                  value={form.hosted_by}
                  onChange={(e) => setForm({ ...form, hosted_by: e.target.value })}
                  className="w-full text-sm px-2 py-2 border rounded bg-white"
                  data-testid="hosting-hosted-by"
                >
                  {HOSTED_BY.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Amount (₹)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="hosting-amount" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="hosting-phone" />
              </div>
              <div>
                <Label className="text-xs">Hosting Date</Label>
                <Input type="date" value={form.hosting_date || ''} onChange={(e) => setForm({ ...form, hosting_date: e.target.value })} data-testid="hosting-date" />
              </div>
              <div>
                <Label className="text-xs">Last Renewal Date</Label>
                <Input type="date" value={form.last_renewal_date || ''} onChange={(e) => setForm({ ...form, last_renewal_date: e.target.value })} data-testid="hosting-renewal-date" />
              </div>
              <div>
                <Label className="text-xs">Expiring Date *</Label>
                <Input type="date" value={form.expiring_date || ''} onChange={(e) => setForm({ ...form, expiring_date: e.target.value })} data-testid="hosting-expiring-date" />
              </div>
              <div>
                <Label className="text-xs">Linked Client (optional)</Label>
                <SearchableSelect
                  testId="hosting-client"
                  value={form.client_id || ''}
                  onChange={(id) => setForm({ ...form, client_id: id })}
                  placeholder="— none —"
                  options={clients.map((c) => {
                    const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                    return {
                      value: c.id,
                      label: fullName || c.company_name || '(unnamed)',
                      sublabel: c.company_name && fullName ? c.company_name : (c.email || ''),
                      search: `${fullName} ${c.company_name || ''} ${c.email || ''}`,
                    };
                  })}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Remarks</Label>
                <textarea
                  value={form.remarks || ''}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={3}
                  className="w-full text-sm border rounded px-3 py-2"
                  data-testid="hosting-remarks"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50">
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" onClick={save} className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="hosting-save">
                {editing ? 'Save changes' : 'Add Hosting'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
